import { zodResolver } from '@hookform/resolvers/zod'
import { Divider, Link as MUILink } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { UserActionsContext } from 'components/User/UserActionsContext'
import { Button } from 'components/common/Button'
import { ButtonVariant } from 'components/common/Button/ButtonVariant'
import { GreyText } from 'components/common/common.style'
import { SignInFormSchema } from 'services/validation/auth/signIn'
import { AuthModal } from './AuthModal'
import {
  LinkWithoutDecorationFullWidth,
  SignInFlex,
  TitleText
} from './AuthModal.style'
import { AuthModalContext } from './AuthModalContext'
import { SSOButtons } from './SSOButtons'
import { SignInForm } from './SignInForm'
import { SignInFormValues } from './SignInFormValues'
import { FullWidth } from './SignInModal.style'

export const SignInModal = () => {
  const {
    modalParams: { redirectUrl },
    setModalParams
  } = useContext(AuthModalContext)

  const displayContinueAsGuest = redirectUrl.includes('/checkout/')
  const { eventId } = useContext(UserActionsContext)

  const { t } = useTranslation('authentication')

  const methods = useForm<SignInFormValues>({
    resolver: zodResolver(SignInFormSchema()),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  const signInRedirect = {
    callbackUrl: eventId
      ? `/api/event/save-event-and-redirect?eventId=${eventId}&redirectTo=${encodeURI(
          redirectUrl
        )}`
      : redirectUrl
  }

  return (
    <AuthModal modalState="SIGNIN">
      <TitleText>
        {displayContinueAsGuest ? t('optionTitle') : t('title')}
      </TitleText>
      {displayContinueAsGuest && (
        <>
          <LinkWithoutDecorationFullWidth href={redirectUrl}>
            <Button
              label={t('continueAsGuest')}
              fullWidth
              variant={ButtonVariant.Primary}
            />
          </LinkWithoutDecorationFullWidth>
          <Divider
            sx={{
              width: '100%'
            }}
          >
            {t('or')}
          </Divider>
        </>
      )}
      <SignInFlex>
        <GreyText>{t('login.description')}</GreyText>
        <FullWidth>
          <FormProvider {...methods}>
            <SignInForm />
          </FormProvider>
        </FullWidth>
        <GreyText>
          <MUILink
            color="inherit"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              setModalParams({
                state: 'FORGOT_PASSWORD',
                redirectUrl,
                email: methods.getValues('email')
              })
            }
          >
            {t('forgotPassword.title')}
          </MUILink>
        </GreyText>
        <SSOButtons signInRedirect={signInRedirect} />
        <Divider
          sx={{
            width: '100%',
            paddingBottom: '10px'
          }}
        >
          {t('or')}
        </Divider>
        <FullWidth>
          <Button
            variant={ButtonVariant.Secondary}
            fullWidth
            label={t('signUp')}
            onClick={() =>
              setModalParams({
                state: 'SIGNUP',
                redirectUrl
              })
            }
          />
        </FullWidth>
      </SignInFlex>
    </AuthModal>
  )
}
